import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, Users, Eye, AlertTriangle, Calendar, Download, X, Search, Wifi, RefreshCw,
} from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Line,
} from 'recharts';
import { researchAPI, adminAPI, authAPI } from '../services/api';
import type { ActivityTimeline, RegistrationCode } from '../types';
import CountryFilter, { type Country } from '../components/CountryFilter';

type Period = '7d' | '14d' | '30d' | 'custom';

const PLATFORM_COLORS = {
  youtube:   '#ef4444',
  facebook:  '#3b82f6',
  instagram: '#ec4899',
  x_twitter: '#525252',
  tiktok:    '#06b6d4',
  other:     '#9ca3af',
};

// Display labels for the SNS column in the user-ranking table.
const SNS_LABEL: Record<string, string> = {
  youtube:   'YouTube',
  facebook:  'Facebook',
  instagram: 'Instagram',
  x_twitter: 'X',
  tiktok:    'TikTok',
};

const CATEGORY_COLORS = {
  illegal_gambling: '#ef4444',
  pachinko:         '#f59e0b',
  public_gambling:  '#3b82f6',
  game_gambling:    '#8b5cf6',
  gacha_premium:    '#ec4899',
};

function jstDateString(offsetDays = 0): string {
  const jstMs = Date.now() + 9 * 3600 * 1000 + offsetDays * 86400000;
  return new Date(jstMs).toISOString().split('T')[0];
}

function rangeForPeriod(period: Exclude<Period, 'custom'>): { from: string; to: string } {
  const to = jstDateString(0);
  const daysBack = period === '7d' ? 6 : period === '14d' ? 13 : 29;
  const from = jstDateString(-daysBack);
  return { from, to };
}

function formatDateShort(d: string): string {
  // "2026-04-23" -> "04/23"
  return d.length >= 10 ? d.slice(5).replace('-', '/') : d;
}

function formatDateTime(d: string | null): string {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleString('ja-JP', {
      year: '2-digit', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return d;
  }
}

export default function ResearchActivity() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const urlDateFrom = searchParams.get('dateFrom');
  const urlDateTo = searchParams.get('dateTo');

  // If a dashboard drill-down passed dateFrom/dateTo, honor them as a custom
  // range; otherwise fall back to the default 14d preset. (2026-07-03: bandwidth
  // now reads the pre-aggregated wg_peer_bw_daily rollup instead of re-scanning
  // millions of wireguard_peer_snapshots per request, so 14d/30d are fast again —
  // the temporary 7d-only stop-gap has been reverted.)
  const [period, setPeriod] = useState<Period>(urlDateFrom && urlDateTo ? 'custom' : '14d');
  const initialRange = rangeForPeriod('14d');
  const [dateFrom, setDateFrom] = useState(urlDateFrom || initialRange.from);
  const [dateTo, setDateTo] = useState(urlDateTo || initialRange.to);
  const [country, setCountry] = useState<Country>(() => {
    const v = searchParams.get('country');
    return v === 'JP' || v === 'VN' ? v : 'all';
  });

  const [scope, setScope] = useState<'all' | 'selected'>('all');
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [codeSearch, setCodeSearch] = useState('');

  const [codes, setCodes] = useState<RegistrationCode[]>([]);
  const [data, setData] = useState<ActivityTimeline | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Daily-activity CSV (same endpoint shipped in eb92e2a)
  const [dailyCsvDate, setDailyCsvDate] = useState(jstDateString(-1));
  const [downloadingDailyCsv, setDownloadingDailyCsv] = useState(false);

  // Role-aware UI: sys-operator only sees the daily CSV download row.
  // Default null while the auth check is in flight so we render the
  // simplified view first and reveal the rich UI only after role lands.
  const [role, setRole] = useState<string | null>(null);
  const isSasadmin = role === 'sasadmin';

  useEffect(() => {
    let cancelled = false;
    authAPI.checkAdminAuth().then(res => {
      if (!cancelled && res.authenticated && res.user) {
        setRole(res.user.role ?? 'sasadmin');
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // --- load registration codes once (for the user picker, sasadmin only) ---
  useEffect(() => {
    if (!isSasadmin) return;
    adminAPI.getCodes().then(setCodes).catch(() => setCodes([]));
  }, [isSasadmin]);

  // --- apply period preset ---
  useEffect(() => {
    if (period === 'custom') return;
    const r = rangeForPeriod(period);
    setDateFrom(r.from);
    setDateTo(r.to);
  }, [period]);

  // --- fetch timeline whenever range or scope changes (sasadmin only) ---
  const fetchData = useCallback(async () => {
    if (!isSasadmin) return;
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await researchAPI.getActivityTimeline({
        dateFrom,
        dateTo,
        codes: scope === 'selected' ? selectedCodes : undefined,
        country: country !== 'all' ? country : undefined,
      });
      setData(payload);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isSasadmin, dateFrom, dateTo, scope, selectedCodes, country]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // --- chart data (memoized) ---
  const chartData = useMemo(() => {
    if (!data) return [];
    return data.timeseries.map(p => ({
      date: formatDateShort(p.date),
      fullDate: p.date,
      activeUsers: p.activeUsers,
      youtube:   p.byPlatform.youtube,
      facebook:  p.byPlatform.facebook,
      instagram: p.byPlatform.instagram,
      x_twitter: p.byPlatform.x_twitter,
      tiktok:    p.byPlatform.tiktok,
      other:     p.byPlatform.other,
      illegal_gambling: p.byCategory.illegal_gambling,
      pachinko:         p.byCategory.pachinko,
      public_gambling:  p.byCategory.public_gambling,
      game_gambling:    p.byCategory.game_gambling,
      gacha_premium:    p.byCategory.gacha_premium,
    }));
  }, [data]);

  const platformMix = useMemo(() => {
    if (!data) return [];
    const totals = { youtube: 0, facebook: 0, instagram: 0, x_twitter: 0, tiktok: 0, other: 0 };
    data.timeseries.forEach(p => {
      totals.youtube   += p.byPlatform.youtube;
      totals.facebook  += p.byPlatform.facebook;
      totals.instagram += p.byPlatform.instagram;
      totals.x_twitter += p.byPlatform.x_twitter;
      totals.tiktok    += p.byPlatform.tiktok;
      totals.other     += p.byPlatform.other;
    });
    return (Object.entries(totals) as [keyof typeof totals, number][])
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ name: k === 'x_twitter' ? 'X' : k[0].toUpperCase() + k.slice(1), value: v, key: k }));
  }, [data]);

  const filteredCodes = useMemo(() => {
    const q = codeSearch.trim().toLowerCase();
    if (!q) return codes.slice(0, 30);
    return codes.filter(c =>
      c.code.toLowerCase().includes(q) || (c.memberName || '').toLowerCase().includes(q)
    ).slice(0, 30);
  }, [codes, codeSearch]);

  // --- handlers ---
  function handleSelectUser(code: string) {
    setScope('selected');
    setSelectedCodes([code]);
  }
  function handleAddCode(code: string) {
    if (!selectedCodes.includes(code)) setSelectedCodes([...selectedCodes, code]);
  }
  function handleRemoveCode(code: string) {
    setSelectedCodes(selectedCodes.filter(c => c !== code));
  }
  function handleClearSelection() {
    setScope('all');
    setSelectedCodes([]);
  }

  async function handleDownloadDailyCSV() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dailyCsvDate)) return;
    setDownloadingDailyCsv(true);
    try {
      const res = await fetch(`/api/admin/export/daily-activity?date=${dailyCsvDate}`, { credentials: 'include' });
      if (!res.ok) { alert('CSV download failed'); return; }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `daily_activity_${dailyCsvDate.replace(/-/g, '')}.csv`;
      link.click();
    } finally {
      setDownloadingDailyCsv(false);
    }
  }

  const hasGambling = (data?.summary.totalGambling ?? 0) > 0;

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612]">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <TrendingUp className="w-7 h-7 text-teal-500" />
            📈 {t('activity.title', 'ボランティア活動の推移')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('activity.subtitle', '期間内の広告キャプチャ・アクティブユーザー・ギャンブル広告の推移')}
          </p>
        </div>

        {/* Error banner — shown up top so the user notices immediately */}
        {error && (
          <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-red-800 dark:text-red-200 text-sm">
                {t('activity.errorTitle', 'データを読み込めませんでした')}
              </div>
              <div className="text-sm text-red-700 dark:text-red-300 mt-0.5">{error}</div>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              aria-label="dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            {isSasadmin && (
              <>
                <CountryFilter value={country} onChange={setCountry} />
                <span className="w-px h-6 bg-gray-300 dark:bg-warm-gray-600 mx-1" />
                {/* Period presets — 14d/30d restored 2026-07-03: bandwidth now reads
                    the wg_peer_bw_daily rollup, so wider windows are fast again. */}
                <div className="flex gap-2">
                  {(['7d', '14d', '30d'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        period === p
                          ? 'bg-fuchsia-600 text-white'
                          : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                      }`}
                    >
                      {t(`activity.period${p}`, p)}
                    </button>
                  ))}
                  <button
                    onClick={() => setPeriod('custom')}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      period === 'custom'
                        ? 'bg-fuchsia-600 text-white'
                        : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                    }`}
                  >
                    <Calendar className="w-4 h-4 inline -mt-0.5" /> {t('activity.periodCustom', 'カスタム範囲')}
                  </button>
                </div>

                {period === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={dateFrom}
                      max={dateTo}
                      onChange={e => setDateFrom(e.target.value)}
                      className="px-2 py-1 border border-warm-gray-300 rounded text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                    />
                    <span className="text-gray-500">〜</span>
                    <input
                      type="date"
                      value={dateTo}
                      min={dateFrom}
                      onChange={e => setDateTo(e.target.value)}
                      className="px-2 py-1 border border-warm-gray-300 rounded text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                    />
                  </div>
                )}

                {/* Scope */}
                <div className="flex items-center gap-2 pl-3 border-l border-warm-gray-300 dark:border-warm-gray-600">
                  <span className="text-sm text-gray-500">{t('activity.scopeLabel', '対象')}:</span>
                  <button
                    onClick={handleClearSelection}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      scope === 'all'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                    }`}
                  >
                    {t('activity.scopeAll', '全員')}
                  </button>
                  <button
                    onClick={() => setScope('selected')}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      scope === 'selected'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 dark:bg-warm-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-warm-gray-600'
                    }`}
                  >
                    {t('activity.scopeSelected', '選択ユーザー')}
                    {selectedCodes.length > 0 && ` (${selectedCodes.length})`}
                  </button>
                </div>
              </>
            )}

            {/* Right-aligned: refresh (sasadmin) + daily CSV (always visible) */}
            <div className={`${isSasadmin ? 'ml-auto' : ''} flex items-center gap-2 flex-wrap`}>
              {isSasadmin && (
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded text-sm font-semibold transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  {t('common.refresh', '更新')}
                </button>
              )}
              <div className={`flex items-center gap-1 ${isSasadmin ? 'pl-2 border-l border-warm-gray-300 dark:border-warm-gray-600' : ''}`}>
                <input
                  type="date"
                  value={dailyCsvDate}
                  onChange={e => setDailyCsvDate(e.target.value)}
                  className="px-2 py-1 border border-warm-gray-300 rounded text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                />
                <button
                  onClick={handleDownloadDailyCSV}
                  disabled={downloadingDailyCsv}
                  className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded font-semibold transition-colors text-sm"
                  title={t('dashboard.dailyCsvHint', '日次活動CSV') as string}
                >
                  <Download className="w-4 h-4" />
                  <span>{t('dashboard.dailyCsvButton', '日次活動CSV')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Chip selector (only when scope = selected, sasadmin only) */}
          {isSasadmin && scope === 'selected' && (
            <div className="mt-3 pt-3 border-t border-warm-gray-200 dark:border-warm-gray-700">
              <div className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={codeSearch}
                  onChange={e => setCodeSearch(e.target.value)}
                  placeholder={t('activity.selectUserPlaceholder', 'コードまたは氏名で検索...') as string}
                  className="flex-1 max-w-sm px-2 py-1 border border-warm-gray-300 rounded text-sm dark:bg-warm-gray-700 dark:border-warm-gray-600 dark:text-white"
                />
                {selectedCodes.length > 0 && (
                  <button
                    onClick={handleClearSelection}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    ← {t('activity.clearSelection', '全員に戻る')}
                  </button>
                )}
              </div>
              {/* Selected chips */}
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedCodes.map(c => {
                  const meta = codes.find(k => k.code === c);
                  return (
                    <span key={c} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 dark:bg-teal-900/40 text-teal-900 dark:text-teal-200 rounded text-xs">
                      {c}{meta?.memberName ? ` (${meta.memberName})` : ''}
                      <button onClick={() => handleRemoveCode(c)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
              {/* Suggestion list */}
              {codeSearch && (
                <div className="max-h-40 overflow-y-auto border border-warm-gray-200 dark:border-warm-gray-700 rounded">
                  {filteredCodes.length === 0 && (
                    <div className="px-2 py-1 text-xs text-gray-400">該当なし</div>
                  )}
                  {filteredCodes.map(c => (
                    <button
                      key={c.code}
                      onClick={() => { handleAddCode(c.code); setCodeSearch(''); }}
                      disabled={selectedCodes.includes(c.code)}
                      className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 dark:hover:bg-warm-gray-700 disabled:opacity-40"
                    >
                      <span className="font-mono text-xs">{c.code}</span>
                      {c.memberName && <span className="ml-2 text-gray-500">· {c.memberName}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* KPI cards (sasadmin only) */}
        {isSasadmin && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 border-l-4 border-teal-500">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
              <Users className="w-4 h-4" /> {t('activity.kpiActiveUsers', 'アクティブユーザー')}
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {data?.summary.activeUsers ?? '—'}
            </div>
          </div>
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 border-l-4 border-blue-500">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
              <Eye className="w-4 h-4" /> {t('activity.kpiTotalAds', '広告総数')}
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {(data?.summary.totalAds ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 border-l-4 border-red-500">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
              <AlertTriangle className="w-4 h-4" /> {t('activity.kpiGamblingAds', 'ギャンブル広告')}
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {(data?.summary.totalGambling ?? 0).toLocaleString()}
              {data && data.summary.gamblingPct > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({data.summary.gamblingPct}%)
                </span>
              )}
            </div>
          </div>
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 border-l-4 border-emerald-500">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
              <Calendar className="w-4 h-4" /> {t('activity.kpiAvgActiveDays', '平均アクティブ日数/人')}
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {data?.summary.avgActiveDaysPerUser ?? '—'}
            </div>
          </div>
          <div
            className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-4 border-l-4 border-indigo-500"
            title={t('activity.kpiSilentVpnHint', '広告0だが10MB以上の通信量があるユーザー') as string}
          >
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-xs mb-1">
              <Wifi className="w-4 h-4" /> {t('activity.kpiSilentVpnUsers', '通信のみ利用者')}
            </div>
            <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {data?.summary.silentVpnUsers ?? '—'}
              {data && data.summary.totalTrafficMB > 0 && (
                <span className="ml-2 text-xs font-normal text-gray-500">
                  ({(data.summary.totalTrafficMB >= 1024
                    ? (data.summary.totalTrafficMB / 1024).toFixed(1) + ' GB'
                    : data.summary.totalTrafficMB.toFixed(1) + ' MB')} 通信量)
                </span>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Chart 1: stacked area + active users line (sasadmin only) */}
        {isSasadmin && (
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                📊 {t('activity.chart1Title', '広告キャプチャ推移')}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {t('activity.chart1Subtitle', 'プラットフォーム別(積み上げ) × アクティブユーザー数')}
              </p>
            </div>
          </div>
          {loading && !data ? (
            <div className="py-20 text-center text-gray-400">{t('activity.loadingData', 'データ読み込み中...')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="ads" tick={{ fontSize: 12 }} />
                <YAxis yAxisId="users" orientation="right" tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="ads" type="monotone" dataKey="youtube"   name="YouTube"  stackId="p" fill={PLATFORM_COLORS.youtube}   stroke={PLATFORM_COLORS.youtube}   fillOpacity={0.7} isAnimationActive={false} />
                <Area yAxisId="ads" type="monotone" dataKey="facebook"  name="Facebook" stackId="p" fill={PLATFORM_COLORS.facebook}  stroke={PLATFORM_COLORS.facebook}  fillOpacity={0.7} isAnimationActive={false} />
                <Area yAxisId="ads" type="monotone" dataKey="instagram" name="Instagram" stackId="p" fill={PLATFORM_COLORS.instagram} stroke={PLATFORM_COLORS.instagram} fillOpacity={0.7} isAnimationActive={false} />
                <Area yAxisId="ads" type="monotone" dataKey="x_twitter" name="X"         stackId="p" fill={PLATFORM_COLORS.x_twitter} stroke={PLATFORM_COLORS.x_twitter} fillOpacity={0.7} isAnimationActive={false} />
                <Area yAxisId="ads" type="monotone" dataKey="tiktok"    name="TikTok"   stackId="p" fill={PLATFORM_COLORS.tiktok}    stroke={PLATFORM_COLORS.tiktok}    fillOpacity={0.7} isAnimationActive={false} />
                <Area yAxisId="ads" type="monotone" dataKey="other"     name="その他"   stackId="p" fill={PLATFORM_COLORS.other}     stroke={PLATFORM_COLORS.other}     fillOpacity={0.7} isAnimationActive={false} />
                <Line yAxisId="users" type="monotone" dataKey="activeUsers" name={t('activity.kpiActiveUsers', 'アクティブユーザー') as string} stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        )}

        {/* Chart 2 + 3 row (sasadmin only) */}
        {isSasadmin && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Chart 2: gambling category stacked bar */}
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
              🎰 {t('activity.chart2Title', 'ギャンブル広告 カテゴリ別内訳')}
            </h2>
            {hasGambling ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="illegal_gambling" name="違法" stackId="g" fill={CATEGORY_COLORS.illegal_gambling} isAnimationActive={false} />
                  <Bar dataKey="pachinko"         name="パチンコ" stackId="g" fill={CATEGORY_COLORS.pachinko} isAnimationActive={false} />
                  <Bar dataKey="public_gambling"  name="公営"    stackId="g" fill={CATEGORY_COLORS.public_gambling} isAnimationActive={false} />
                  <Bar dataKey="game_gambling"    name="ソシャゲ" stackId="g" fill={CATEGORY_COLORS.game_gambling} isAnimationActive={false} />
                  <Bar dataKey="gacha_premium"    name="ガチャ"  stackId="g" fill={CATEGORY_COLORS.gacha_premium} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-16 text-center text-sm text-gray-400">
                {t('activity.emptyGambling', 'この期間にギャンブル広告は検出されませんでした')}
              </div>
            )}
          </div>

          {/* Chart 3: platform mix pie */}
          <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
              🥧 {t('activity.chart3Title', 'プラットフォーム構成比')}
            </h2>
            {platformMix.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={platformMix} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label isAnimationActive={false}>
                    {platformMix.map((entry) => (
                      <Cell key={entry.key} fill={PLATFORM_COLORS[entry.key as keyof typeof PLATFORM_COLORS]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: any) => (typeof v === 'number' ? v.toLocaleString() : v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="py-16 text-center text-sm text-gray-400">
                {t('activity.emptyUsers', 'この期間にアクティブなユーザーはいません')}
              </div>
            )}
          </div>
        </div>
        )}

        {/* User ranking table (sasadmin only) */}
        {isSasadmin && (
        <div className="bg-white dark:bg-warm-gray-800 rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
              🏆 {t('activity.tableTitle', 'ユーザー別ランキング')}
            </h2>
            <span className="text-xs text-gray-500">
              {t('activity.clickRowHint', 'ユーザーをクリックすると、そのユーザーのみにフィルタされます')}
            </span>
          </div>
          {selectedCodes.length > 0 && (
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t('activity.filteringBy', 'フィルタ中')}:</span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 text-xs font-medium">
                <span className="font-mono">{selectedCodes[0]}</span>
                {(() => {
                  const n = data?.users.find(u => u.code === selectedCodes[0])?.memberName;
                  return n ? <span>({n})</span> : null;
                })()}
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="ml-0.5 hover:text-teal-950 dark:hover:text-white"
                  title={t('activity.clearSelection', '全員に戻る') as string}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </span>
              <button
                type="button"
                onClick={handleClearSelection}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
              >
                ← {t('activity.clearSelection', '全員に戻る')}
              </button>
            </div>
          )}
          {data && data.users.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-warm-gray-200 dark:border-warm-gray-700 rounded">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-kido-50 dark:bg-warm-gray-700 z-10">
                  <tr className="border-b border-warm-gray-200 dark:border-warm-gray-600">
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-28">{t('activity.tableHeaderLifecycle', 'ライフサイクル')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-36">{t('activity.tableHeaderCode', 'コード')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-28">{t('activity.tableHeaderName', '氏名')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-36">{t('activity.tableHeaderSns', 'SNS')}</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-28">{t('activity.tableHeaderAds', '広告数(範囲内)')}</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-24">{t('activity.tableHeaderGambling', 'ギャンブル')}</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-20">{t('activity.tableHeaderRxMb', '受信MB')}</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-20">{t('activity.tableHeaderTxMb', '送信MB')}</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-24">{t('activity.tableHeaderActiveDays', 'アクティブ日数')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700 dark:text-gray-300 w-40">{t('activity.tableHeaderLastEvent', '最終イベント')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.map(u => (
                    <tr
                      key={u.code}
                      onClick={() => handleSelectUser(u.code)}
                      className={`cursor-pointer border-b border-warm-gray-100 dark:border-warm-gray-700 hover:bg-teal-50 dark:hover:bg-warm-gray-700 ${
                        selectedCodes.includes(u.code) ? 'bg-teal-50 dark:bg-warm-gray-700/50' : ''
                      }`}
                    >
                      <td className="py-1.5 px-3">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${
                          u.lifecycleStage === 'active' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : u.lifecycleStage === 'started_using' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
                          : u.lifecycleStage === 'registered' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-200'
                        }`}>
                          {u.lifecycleStage}
                        </span>
                      </td>
                      <td className="py-1.5 px-3 font-mono text-xs">
                        {u.code}
                        {u.adsInRange === 0 && (u.rxBytesInRange + u.txBytesInRange) > 10 * 1024 * 1024 && (
                          <span className="ml-1 inline-block px-1 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200 rounded text-[10px]" title={t('activity.kpiSilentVpnHint', '広告0だが10MB以上の通信量') as string}>
                            {t('activity.silentVpnBadge', '📶 利用あり (広告0)')}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 truncate" title={u.memberName || ''}>{u.memberName || '—'}</td>
                      <td className="py-1.5 px-3 text-gray-700 dark:text-gray-300 text-xs">
                        {(u.snsPlatformsInRange || []).length > 0
                          ? (u.snsPlatformsInRange || []).map(p => SNS_LABEL[p] || p).join(', ')
                          : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right font-semibold">{u.adsInRange.toLocaleString()}</td>
                      <td className={`py-1.5 px-3 text-right ${u.gamblingInRange > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-400'}`}>
                        {u.gamblingInRange > 0 ? u.gamblingInRange.toLocaleString() : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">
                        {u.rxBytesInRange > 0 ? (u.rxBytesInRange / (1024 * 1024)).toFixed(1) : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">
                        {u.txBytesInRange > 0 ? (u.txBytesInRange / (1024 * 1024)).toFixed(1) : '—'}
                      </td>
                      <td className="py-1.5 px-3 text-right">{u.activeDaysInRange}</td>
                      <td className="py-1.5 px-3 text-gray-600 dark:text-gray-400">{formatDateTime(u.lastEventInRange)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-12 text-center text-sm text-gray-400">
              {t('activity.emptyUsers', 'この期間にアクティブなユーザーはいません')}
            </div>
          )}
        </div>
        )}

      </div>
    </div>
  );
}
